import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

/** What kind of work a step was. Numbers, because the API sends every enum as one. */
export enum AssistantTurnStepKind {
  Retrieval = 0,
  Capability = 1,
  Verification = 2,
  Retry = 3,
}

/** How a step ended. `Completed` is 0, so read it as a number rather than for truthiness. */
export enum AssistantTurnStepOutcome {
  Completed = 0,
  Refused = 1,
  Failed = 2,
}

export type AssistantTurnStepJSON = z.infer<typeof AssistantTurnStepSchema.AssistantTurnStepJSONZ>;
export type AssistantTurnStepParams = z.infer<typeof AssistantTurnStepSchema.AssistantTurnStepZ>;

/**
 * One thing a turn did on the way to its answer (#7284).
 *
 * The label is the server's, from a fixed list it owns - never the model's prose, and never
 * anything a lookup was asked or answered - so the panel renders it as it arrives.
 */
export default class AssistantTurnStepSchema {
  public static readonly AssistantTurnStepJSONZ = z.object({
    kind: z.enum(AssistantTurnStepKind),
    label: z.string().min(1),
    outcome: z.enum(AssistantTurnStepOutcome).nullish(),
    // Calls of one capability in a row, or the citations the check read. Absent otherwise.
    count: z.number().int().positive().nullish(),
  });

  public static readonly AssistantTurnStepZ = z.object({
    kind: z.enum(AssistantTurnStepKind),
    label: z.string().min(1),
    outcome: z.enum(AssistantTurnStepOutcome),
    count: z.number().int().positive().optional(),
  });

  public static validate(values: unknown): AssistantTurnStepParams {
    return parseOrThrow(AssistantTurnStepSchema.AssistantTurnStepZ, values, {
      logError: true,
      schemaName: "AssistantTurnStepSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is AssistantTurnStepParams {
    try {
      AssistantTurnStepSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  /** Params from JSON the caller has already validated, as part of a reply's one parse. */
  public static decodeParsed(json: AssistantTurnStepJSON): AssistantTurnStepParams {
    return {
      kind: json.kind,
      label: json.label,
      outcome: json.outcome ?? AssistantTurnStepOutcome.Completed,
      count: json.count ?? undefined,
    };
  }

  public static deserialize(json: AssistantTurnStepJSON): AssistantTurnStepParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(AssistantTurnStepSchema.AssistantTurnStepJSONZ, json, {
      logError: true,
      schemaName: "AssistantTurnStepSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return AssistantTurnStepSchema.decodeParsed(parsed);
  }

  public static serialize(step: AssistantTurnStepParams): AssistantTurnStepJSON {
    if (isNullOrUndefined(step)) return step;
    const parsed = AssistantTurnStepSchema.validate(step);
    return {
      kind: parsed.kind,
      label: parsed.label,
      outcome: parsed.outcome,
      count: parsed.count,
    };
  }
}
